<template>
  <component :is="useCard">
    <div class="map-layout">
      <div class="map-scroll">
        <div class="map" :style="{ width: `${W}px`, height: `${H}px` }">
          <svg class="edges" :width="W" :height="H">
            <defs>
              <marker v-for="(color, key) in colors" :key="key" :id="`eco977-arr-${key}`"
                      viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                <path d="M 0 0 L 10 5 L 0 10 z" :fill="color"/>
              </marker>
            </defs>
            <path v-for="(edge, index) in edges" :key="index"
                  :d="edge.d" fill="none"
                  :stroke="colors[edge.c]" :stroke-width="edge.w || 1.5"
                  :stroke-dasharray="edge.dash"
                  :marker-end="edge.arrow ? `url(#eco977-arr-${edge.c})` : null"
                  :class="{ dim: hoverKey && edge.k !== hoverKey }"/>
          </svg>

          <div v-for="header in headers" :key="header.text" class="col-header" :style="rect(header)">
            [{{ header.text }}]
          </div>

          <div v-for="node in nodes" :key="node.code" class="node" :class="[node.cls, { dim: nodeDimmed(node.code) }]"
               :style="rect(node)" @click="onClickNode(node.code)">
            <span>
              {{ node.label }}<br>
              <template v-if="node.sub">{{ node.sub }}<br></template>
              ({{ node.code }})
            </span>
          </div>

          <!-- 생산품 -->
          <div class="product-table" :class="{ dim: hoverKey && hoverKey !== 'product' }" :style="rect(product)">
            <div v-for="cell in product.cells" :key="cell.code" class="cell" @click="onClickNode(cell.code)">
              {{ cell.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- 범례: 도면 우측에서 스크롤을 따라다님 -->
      <div class="legend-wrap">
        <div class="legend">
          <h1>※ 범 례</h1>
          <div class="legend-grid">
            <div v-for="item in legendItems" :key="item.key" class="legend-row cursor-pointer"
                 :class="{ dim: hoverKey && hoverKey !== item.key }"
                 @mouseenter="hoverKey = item.key" @mouseleave="hoverKey = ''">
              <div class="name">{{ item.name }}</div>
              <div class="line">
                <svg width="90" height="10">
                  <path d="M 0 5 H 82" fill="none" :stroke="colors[item.c]" stroke-width="1.5"
                        :stroke-dasharray="item.dash" :marker-end="`url(#eco977-arr-${item.c})`"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
export default {
  props: {
    card: {
      type: Boolean,
      default: true
    },
  },
  computed: {
    useCard() {
      if (this.card) {
        return 'vx-card'
      }

      return 'div'
    }
  },
  data() {
    return {
      W: 1000,
      H: 500,
      colors: {
        black: '#000000',
        orange: '#ED7D31',
      },
      headers: [
        { text: '유틸리티공정', x: 40, y: 8, w: 190, h: 30 },
        { text: '제품제조공정', x: 400, y: 8, w: 230, h: 30 },
        { text: '환경오염물질 처리공정', x: 740, y: 8, w: 230, h: 30 },
        { text: '기타공정', x: 400, y: 370, w: 230, h: 30 },
      ],
      nodes: [
        { code: 'PU-01', label: '원수 정수공정', x: 40, y: 60, w: 190, h: 56 },
        { code: 'PU-02', label: '용수 정수공정', x: 40, y: 136, w: 190, h: 56 },
        { code: 'PU-03', label: 'Roll 연마공정', x: 40, y: 212, w: 190, h: 56 },
        { code: 'PU-04', label: 'Knife 연마공정', x: 40, y: 288, w: 190, h: 56 },
        { code: 'PU-05', label: '약품투입공정', x: 40, y: 364, w: 190, h: 56 },

        { code: 'P-01', label: '열간압연/압출공정', x: 400, y: 60, w: 230, h: 90, cls: 'gray' },
        { code: 'P-02', label: '제품 시험공정', x: 400, y: 250, w: 230, h: 90, cls: 'gray' },
        { code: 'PE-01', label: '위험물 저장공정', x: 400, y: 410, w: 230, h: 56 },

        { code: 'PW-01', label: '폐수처리공정', sub: '(물리화학적 폐수처리)', x: 740, y: 60, w: 230, h: 64 },
        { code: 'PW-02', label: '폐기물 보관공정', sub: '(지정&일반)', x: 740, y: 150, w: 230, h: 64 },
        { code: 'PW-03', label: '비점오염 저감공정', x: 740, y: 240, w: 230, h: 56 },
      ],
      product: {
        x: 550, y: 160, w: 140, h: 80,
        cells: [
          { label: '생산품', code: 'P-01_생산품' },
          { label: '처리', code: 'P-01_처리' },
          { label: '후판', code: 'P-01_후판' },
          { label: '판매', code: 'P-01_판매' },
        ]
      },
      legendItems: [
        { key: 'raw', name: '원료(Slab), 공정수', c: 'black' },
        { key: 'product', name: '생산품(후판)', c: 'orange' },
        { key: 'waste', name: '환경오염물질(폐수, 대기오염물질 등)', c: 'black', dash: '6 4' },
      ],
      // 범례 hover 시 강조할 관련 공정 (카테고리 → 공정코드)
      related: {
        raw: ['PU-01', 'PU-02', 'PU-03', 'PU-04', 'PU-05', 'P-01'],
        product: ['P-01'],
        waste: ['P-01', 'PW-01', 'PW-02', 'PW-03'],
      },
      hoverKey: '',
      edges: [
        // 원료(Slab), 공정수 (흑색 실선)
        { k: 'raw', c: 'black', d: 'M 230 88 H 300' },
        { k: 'raw', c: 'black', d: 'M 230 164 H 300' },
        { k: 'raw', c: 'black', d: 'M 230 240 H 300' },
        { k: 'raw', c: 'black', d: 'M 230 316 H 300' },
        { k: 'raw', c: 'black', d: 'M 230 392 H 300' },
        { k: 'raw', c: 'black', d: 'M 300 88 V 392' },
        { k: 'raw', c: 'black', d: 'M 300 105 H 396', arrow: true },

        // 생산품(후판) (주황색 실선)
        { k: 'product', c: 'orange', d: 'M 515 150 V 200 H 544', arrow: true },
        // 기존 공정도에는 P-01 → P-02(제품 시험공정) 연결 표기가 없어 주석 처리
        // { k: 'product', c: 'orange', d: 'M 460 150 V 246', arrow: true },

        // 환경오염물질 (흑색 점선)
        { k: 'waste', c: 'black', d: 'M 630 92 H 736', dash: '6 4', arrow: true },
        { k: 'waste', c: 'black', d: 'M 700 92 V 268', dash: '6 4' },
        { k: 'waste', c: 'black', d: 'M 700 182 H 736', dash: '6 4', arrow: true },
        { k: 'waste', c: 'black', d: 'M 700 268 H 736', dash: '6 4', arrow: true },
      ],
    }
  },
  methods: {
    isRelated(code) {
      const codes = this.related[this.hoverKey]
      return codes ? codes.includes(code) : true
    },
    nodeDimmed(code) {
      return !!this.hoverKey && !this.isRelated(code)
    },
    rect(o) {
      return {
        left: `${o.x}px`,
        top: `${o.y}px`,
        width: `${o.w}px`,
        height: `${o.h}px`,
      }
    },
    onClickNode(itemId) {
      this.$emit('node', itemId)
    }
  }
}
</script>

<style lang="scss" scoped>
.map-layout {
  display: flex;
  justify-content: center;
  gap: 24px;
  padding: 2rem 1rem 4rem;
}
.map-scroll {
  min-width: 0;
  overflow-x: auto;
}
.map {
  position: relative;
}
.edges {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;

  path { transition: opacity 0.15s }
  path.dim { opacity: 0.08 }
}
.node, .product-table { transition: opacity 0.15s }
.node.dim, .product-table.dim { opacity: 0.2 }
.col-header {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 1.05rem;
  font-weight: 700;
}
.node {
  position: absolute;
  padding: 4px;
  display: flex;
  border: 1px solid #333333;
  background-color: #ffffff;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: 13px;
  line-height: 1.3;
  cursor: pointer;

  &.gray { background-color: #D9D9D9 }

  &:hover { background-color: #E5E5E5; border: 1px solid #000000 !important; }
}
.product-table {
  position: absolute;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid #333333;
  border-left: 1px solid #333333;

  .cell {
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #333333;
    border-bottom: 1px solid #333333;
    background-color: #ffffff;
    font-size: 13px;
    cursor: pointer;

    &:hover { background-color: #E5E5E5 }
  }
}
.legend-wrap {
  flex: 0 0 300px;
}
.legend {
  position: sticky;
  top: 100px; // 상단 고정 헤더 높이만큼 띄움
  border: 1px solid #333333;
  background-color: #ffffff;
  padding: 8px 12px;

  h1 {
    font-size: 0.95rem;
    font-weight: 700;
    margin-bottom: 6px;
  }
  .legend-grid {
    display: grid;
    row-gap: 2px;
  }
  .legend-row {
    display: grid;
    grid-template-columns: 1fr 96px;
    align-items: center;
    padding: 4px 6px;
    border-radius: 4px;
    cursor: pointer;
    transition: opacity 0.15s, background-color 0.15s;

    &:hover { background-color: #F3F4F6 }
    &.dim { opacity: 0.35 }

    .name { font-size: 12px }
    .line { display: flex; align-items: center; justify-content: flex-end }
  }
}
</style>
